import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'

/**
 * Gedeelde publieke header (huisstijl: wit + border, ink #0A0F0D, groen #3EB489).
 *
 * Props:
 * - links     : [{ to, label }]  navigatielinks (default: Opdrachten/Creators/Pakketten/Bedrijven)
 * - showNav   : boolean          toon nav-links + hamburger (default true)
 * - ctaLabel  : string           label van de groene knop voor niet-ingelogde bezoekers (default "Gratis aanmelden")
 * - ctaTo     : string           route van die knop (default "/register")
 * - rightSlot : ReactNode        optionele extra inhoud rechts (desktop én mobiel), bv. een label
 *
 * Gedrag: sticky top-0 z-50; hamburger < md met aria-expanded/aria-controls; paneel sluit op
 * link-klik, Escape, klik buiten en routewissel; body-scroll-lock zolang het paneel open is.
 * Ingelogd → "Dashboard"-knop i.p.v. Inloggen/aanmelden (zelfde auth-context als de pagina's).
 */

export const PUBLIC_NAV_LINKS = [
  { to: '/projects', label: 'Opdrachten' },
  { to: '/creators', label: 'Creators' },
  { to: '/pakketten', label: 'Pakketten' },
  { to: '/companies', label: 'Bedrijven' },
]

const MENU_ID = 'public-header-mobile-menu'

const PublicHeader = ({
  links = PUBLIC_NAV_LINKS,
  showNav = true,
  ctaLabel = 'Gratis aanmelden',
  ctaTo = '/register',
  rightSlot = null,
}) => {
  const { isAuthenticated } = useAuth()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const headerRef = useRef(null)
  const toggleRef = useRef(null)

  const close = useCallback(() => setOpen(false), [])

  // Sluiten bij routewissel
  useEffect(() => { setOpen(false) }, [location.pathname])

  // Escape, klik buiten, body-scroll-lock
  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); toggleRef.current?.focus() } }
    const onPointer = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('touchstart', onPointer, { passive: true })
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('touchstart', onPointer)
      document.body.style.overflow = prevOverflow
    }
  }, [open])

  // Paneel sluiten zodra we naar desktop gaan (voorkomt "vastzittende" scroll-lock)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)')
    const onChange = (e) => { if (e.matches) setOpen(false) }
    if (mq.addEventListener) mq.addEventListener('change', onChange)
    else mq.addListener(onChange)
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange)
      else mq.removeListener(onChange)
    }
  }, [])

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(`${to}/`)

  const desktopLink = (to) =>
    `inline-flex items-center min-h-[44px] px-1 text-[15px] font-medium transition ${
      isActive(to) ? 'text-[#3EB489] font-semibold' : 'text-[#1F2937] hover:text-[#3EB489]'
    }`

  const mobileLink = (to) =>
    `flex items-center min-h-[48px] px-4 rounded-xl text-base font-medium transition ${
      isActive(to) ? 'text-[#3EB489] font-semibold bg-[#F8FAFC]' : 'text-[#0A0F0D] hover:bg-[#F8FAFC]'
    }`

  return (
    <header
      ref={headerRef}
      className="sticky top-0 left-0 right-0 z-50 bg-white border-b border-[#E5E7EB]"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-3">
        <Link
          to="/"
          onClick={close}
          className="inline-flex items-center min-h-[44px] text-2xl font-black tracking-tighter text-[#0A0F0D] flex-shrink-0"
          aria-label="UGC4you home"
        >
          UGC<span className="text-[#3EB489]">4you</span>
        </Link>

        {/* Desktop */}
        <nav className="hidden lg:flex items-center gap-6 lg:gap-8" aria-label="Hoofdnavigatie">
          {showNav && links.map(({ to, label }) => (
            <Link key={to} to={to} className={desktopLink(to)}>{label}</Link>
          ))}
          {rightSlot && <div className="text-sm text-[#475569]">{rightSlot}</div>}
          {isAuthenticated ? (
            <Link
              to="/dashboard"
              className="inline-flex items-center min-h-[44px] px-5 py-2 bg-[#3EB489] hover:bg-[#359E78] rounded-full font-semibold text-white transition"
            >
              Dashboard
            </Link>
          ) : (
            <div className="flex items-center gap-2">
              <Link
                to="/login"
                className="inline-flex items-center min-h-[44px] px-3 text-[15px] font-medium text-[#1F2937] hover:text-[#3EB489] transition"
              >
                Inloggen
              </Link>
              <Link
                to={ctaTo}
                className="inline-flex items-center whitespace-nowrap min-h-[44px] px-5 py-2 bg-[#3EB489] hover:bg-[#359E78] rounded-full font-semibold text-white transition"
              >
                {ctaLabel}
              </Link>
            </div>
          )}
        </nav>

        {/* Mobiel: label + hamburger */}
        <div className="flex lg:hidden items-center gap-2">
          {rightSlot && <div className="text-sm text-[#475569]">{rightSlot}</div>}
          {showNav && (
            <button
              ref={toggleRef}
              type="button"
              onClick={() => setOpen(v => !v)}
              aria-expanded={open}
              aria-controls={MENU_ID}
              aria-label={open ? 'Menu sluiten' : 'Menu openen'}
              className="inline-flex items-center justify-center w-11 h-11 -mr-2 rounded-xl text-[#0A0F0D] hover:bg-[#F8FAFC] transition"
            >
              {open ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
            </button>
          )}
          {!showNav && !isAuthenticated && (
            <Link
              to="/login"
              className="inline-flex items-center min-h-[44px] px-3 text-sm font-medium text-[#1F2937] hover:text-[#3EB489] transition"
            >
              Inloggen
            </Link>
          )}
        </div>
      </div>

      {/* Mobiel paneel (inline in de header, over de content heen) */}
      {showNav && open && (
        <>
          <div
            className="lg:hidden fixed inset-0 top-16 bg-[#0A0F0D]/20"
            aria-hidden="true"
            onClick={close}
          />
          <div
            id={MENU_ID}
            className="lg:hidden absolute left-0 right-0 top-full bg-white border-b border-[#E5E7EB] shadow-[0_12px_32px_-12px_rgba(10,15,13,0.18)] max-h-[calc(100vh-4rem)] overflow-y-auto"
          >
            <nav className="px-3 py-3 space-y-1" aria-label="Mobiele navigatie">
              {links.map(({ to, label }) => (
                <Link key={to} to={to} onClick={close} className={mobileLink(to)}>{label}</Link>
              ))}
            </nav>
            <div className="px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] border-t border-[#E5E7EB] flex flex-col gap-2">
              {isAuthenticated ? (
                <Link
                  to="/dashboard"
                  onClick={close}
                  className="flex items-center justify-center min-h-[48px] px-5 bg-[#3EB489] hover:bg-[#359E78] rounded-xl font-semibold text-white transition"
                >
                  Dashboard
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={close}
                    className="flex items-center justify-center min-h-[48px] px-5 bg-white border border-[#E5E7EB] hover:border-[#0A0F0D] rounded-xl font-semibold text-[#0A0F0D] transition"
                  >
                    Inloggen
                  </Link>
                  <Link
                    to={ctaTo}
                    onClick={close}
                    className="flex items-center justify-center min-h-[48px] px-5 bg-[#3EB489] hover:bg-[#359E78] rounded-xl font-semibold text-white transition"
                  >
                    {ctaLabel}
                  </Link>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </header>
  )
}

export default PublicHeader
